import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  IconButton,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Producto } from '../../types';
import { ImageCarousel } from './ImageCarousel';
import { ColorIndicators } from './ColorIndicators';

interface ProductCardProps {
  producto: Producto;
  onEdit: (producto: Producto) => void;
  onDelete: (id: number) => void;
  onViewDetails: (producto: Producto) => void;
}

export function ProductCard({
  producto,
  onEdit,
  onDelete,
  onViewDetails,
}: ProductCardProps) {
  const getStockColorScheme = (
    cantidad: number,
    minimo: number
  ): { bg: string; text: string } => {
    if (cantidad > 50) {
      return { bg: '#eaf5f2', text: '#3d7a6e' };
    } else if (cantidad > 10) {
      return { bg: '#fdf5e0', text: '#8a6e30' };
    } else {
      return { bg: '#fdf0f0', text: '#8a3d3d' };
    }
  };

  return (
    <Card
      role="article"
      aria-label={`Producto: ${producto.nombre}`}
      sx={{
        backgroundColor: '#f7edf5',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        opacity: !producto.activo ? 0.6 : 1,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: 8,
          '& img': {
            transform: 'scale(1.05)',
          },
          '& [role="img"]': {
            transform: 'scale(1.05)',
          },
        },
      }}
    >
      {/* Image Carousel */}
      <Box sx={{ position: 'relative' }}>
        <ImageCarousel
          imagenPrincipal={producto.imagenPrincipal}
          productoId={producto.id}
          cantidadImagenes={producto.cantidadImagenes}
          categoriaColor={producto.categoriaColor}
          height={200}
        />

        {/* Badges */}
        {producto.cantidadStock <= producto.stockMinimo && (
          <Chip
            label="STOCK BAJO"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              backgroundColor: '#8a3d3d',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.7rem',
              letterSpacing: '0.5px',
              animation: 'slideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '@keyframes slideIn': {
                '0%': {
                  opacity: 0,
                  transform: 'translateX(20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
            }}
          />
        )}
        {!producto.activo && (
          <Chip
            label="INACTIVO"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              backgroundColor: '#5a5a5a',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.7rem',
              letterSpacing: '0.5px',
              animation: 'slideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '@keyframes slideIn': {
                '0%': {
                  opacity: 0,
                  transform: 'translateX(20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
            }}
          />
        )}
      </Box>

      {/* Content */}
      <CardContent
        sx={{
          flexGrow: 1,
          pb: 1,
          pt: 1.5,
          px: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        {/* Category Chip */}
        {producto.categoriaNombre && (
          <Chip
            label={producto.categoriaNombre}
            variant="outlined"
            size="small"
            sx={{
              borderColor: `${producto.categoriaColor || '#f0f0f0'}33`,
              backgroundColor: 'transparent',
              color: producto.categoriaColor || '#f0f0f0',
              fontWeight: 500,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              },
            }}
            icon={
              producto.categoriaIcono
                ? (
                    <span
                      style={{
                        fontSize: '1rem',
                        marginRight: '4px',
                      }}
                    >
                      {producto.categoriaIcono}
                    </span>
                  )
                : undefined
            }
          />
        )}

        {/* Product Name */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            margin: 0,
          }}
        >
          {producto.nombre}
        </Typography>

        {/* Product Code */}
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            display: 'block',
            margin: 0,
          }}
        >
          {producto.codigo}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '2.625rem',
            color: '#666',
            fontSize: '0.8rem',
            lineHeight: 1.3125,
            margin: 0,
          }}
        >
          {producto.descripcion || ''}
        </Typography>

        {/* Price */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            color: '#7a3d6e',
            margin: 0,
          }}
        >
          ${producto.precioVenta.toLocaleString('es-CO')}
        </Typography>

        {/* Stock Chip */}
        {(() => {
          const stockColor = getStockColorScheme(
            producto.cantidadStock,
            producto.stockMinimo
          );
          return (
            <Chip
              label={`Stock: ${producto.cantidadStock}`}
              size="small"
              variant="filled"
              sx={{
                backgroundColor: stockColor.bg,
                color: stockColor.text,
                fontWeight: 500,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                },
              }}
            />
          );
        })()}

        {/* Color Indicators */}
        <ColorIndicators colores={producto.colores} />
      </CardContent>

      {/* Actions */}
      <CardActions
        sx={{
          pt: 0,
          pb: 1,
          display: 'flex',
          gap: 0.5,
          justifyContent: 'flex-start',
          transition: 'background-color 0.2s ease-in-out',
        }}
      >
        <IconButton
          size="small"
          onClick={() => onViewDetails(producto)}
          title="Ver detalles"
          aria-label="Ver detalles del producto"
          sx={{
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.15)',
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
            },
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onEdit(producto)}
          title="Editar"
          aria-label="Editar producto"
          sx={{
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.15)',
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
            },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={() => onDelete(producto.id)}
          title="Eliminar"
          aria-label="Eliminar producto"
          sx={{
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.15)',
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
}
